import {Switch} from 'antd'
import {course_getCourseClasses_Api, course_getCourses_Api,} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {branch, compose, renderNothing} from 'recompose'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import {renderIf} from "../../../../Shared";
import {baseFieldTypes} from "../../../../../apis/base/baseFieldTypes";
import {ChipGroup} from "../../../../../components/form/ModernForm";

const FilterSelectField = ({
                               name,
                               value,
                               onChange,
                               options,
                               isLoading,
                               className,
                               ...props
                           }) => (
    <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
        <SelectWidget
            value={_.find(options, {value}) ? value : undefined}
            showSearch={true}
            allowClear={true}
            onChange={(value) => {
                onChange(name, value)
            }}
            loading={!!isLoading}
            optionFilterProp="label"
            className={classNames('CenterSelector w-full', className)}
            options={!!isLoading ? [] : options}
            suffixIcon={
                !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator/>
            }
            {...props}
        />
    </AntdConfigWrapper>
)

const CourseAsyncWrapper = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        renderNothing
    )
)(({owner_id, owner_type, children}) => {
    const [values, setValues] = useState()

    const apiInfo = course_getCourses_Api

    const cacheId = useMemo(
        () =>
            [
                apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
                JSON.stringify(values),
            ].join('/'),
        [values, owner_id, owner_type]
    )

    const {response, isLoading} = useAsyncWithCache({
        values,
        cacheId,
        apiInfo,
        query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
    })

    return _.isFunction(children)
        ? children({
            isLoading,
            items: getResponseItems(response),
            onSearch: (keyword) => {
                setValues({
                    keyword,
                })
            },
        })
        : children
})

const CourseClassAsyncWrapper = compose(
    branch(({course_id}) => _.isEmpty(course_id), renderNothing)
)(({course_id, children}) => {
    const [values, setValues] = useState()

    const apiInfo = course_getCourseClasses_Api

    const cacheId = useMemo(
        () =>
            [apiInfo.path.replace(':id', course_id), JSON.stringify(values)].join(
                '/'
            ),
        [values, course_id]
    )

    const {response, isLoading} = useAsyncWithCache({
        values,
        cacheId,
        apiInfo,
        query: bindQueryParam({id: course_id}),
    })

    return _.isFunction(children)
        ? children({
            isLoading,
            items: getResponseItems(response),
            onSearch: (keyword) => {
                setValues({
                    keyword,
                })
            },
        })
        : children
})

const FilterChip = ({active, onClick, children}) => (
    <button
        type="button"
        onClick={onClick}
        style={{minHeight: 30}}
        className={classNames(
            'px-3 py-1 flex flex-center flex-shrink-0 gap-1 rounded-lg text-sm font-semibold transition-all cursor-pointer',
            active
                ? 'bg-secondary-500 text-white'
                : 'background shadow-sm hover:shadow-md text-slate-600 border border-color-50 hover:border-secondary-500 hover:text-secondary-500'
        )}>
        {children}
    </button>
)

export const StudentFilter = ({
                                  owner_id,
                                  owner_type,
                                  defaultValue,
                                  value: filter = {
                                      course_id: undefined,
                                      course_class_id: undefined,
                                      sub_class_name: undefined,
                                      include_archived: undefined,
                                      active: undefined,
                                  },
                                  onChange: setValues = NullAsDefault,
                              }) => {
    const t = useTranslate()

    const handleChange = useCallback(
        (name, value) => {
            const params = {
                ...filter,
                [name]: value,
            }
            setValues(params)
            return params
        },
        [filter]
    )

    const activeValue = filter?.active == null ? undefined : String(filter.active)
    const isAll = activeValue === undefined || activeValue === ''

    return (
        <div className="flex flex-col items-center gap-2 mt-2">
            <CourseAsyncWrapper
                {...{
                    owner_id,
                    owner_type,
                }}>
                {({items, isLoading}) => {
                    const options = Array.from(items).map((option) => ({
                        value: getId(option),
                        label: getTitle(option),
                    }))
                    return (
                        <div className="flex flex-col gap-1 w-full">
                            <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                                {`${t('course')} : `}
                            </span>
                            <FilterSelectField
                                name="course_id"
                                loading={isLoading}
                                value={filter.course_id}
                                placeholder={t(isLoading ? 'loading...' : 'course')}
                                options={options}
                                onChange={(name, value) => {
                                    const next = handleChange(name, value)
                                    setValues({
                                        ...(next ?? {}),
                                        course_class_id: undefined,
                                    })
                                }}
                            />
                        </div>
                    )
                }}
            </CourseAsyncWrapper>
            <CourseClassAsyncWrapper {...{course_id: filter.course_id}}>
                {({items, isLoading}) => {
                    const options = Array.from(items).map((option) => ({
                        value: getId(option),
                        label: getTitle(option),
                    }))
                    return (
                        <div className="flex flex-col space-y-3 w-full">
                            <div className="flex flex-col gap-1 w-full">
                                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                                    {t('classes')}
                                </span>
                                <FilterSelectField
                                    loading={isLoading}
                                    name="course_class_id"
                                    value={filter.course_class_id}
                                    placeholder={t(isLoading ? 'loading...' : 'classes')}
                                    options={options}
                                    onChange={(name, value) => {
                                        const next = handleChange(name, value)
                                        setValues({
                                            ...(next ?? {}),
                                        })
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-1 w-full">
                              <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                                  {`${t('subclass title')} : `}
                              </span>
                                {renderIf(
                                    filter.course_class_id,
                                    <div className="flex flex-wrap gap-y-1 gap-x-2 text-xs">
                                        {_.flatMap(items.filter(e => e.id === filter.course_class_id), ee => ee.sub_class_names)
                                            .filter(e => !!e)
                                            .map((e, i) => (
                                                    <span key={i}
                                                          className={classNames("px-3 py-1 flex flex-center cursor-pointer rounded-full no-border font-bold",
                                                              filter?.sub_class_name === e ? "bg-primary hover:bg-primary-50 text-white hover:text-primary-600"
                                                                  : "background-200 hover:bg-primary-50 text-color-300 hover:text-primary-600")}
                                                          onClick={() => {
                                                              if (filter?.sub_class_name === e) {
                                                                  handleChange("sub_class_name", null)
                                                              } else {
                                                                  handleChange("sub_class_name", e)
                                                              }
                                                          }}>
                                                  {e}
                                              </span>
                                                )
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }}
            </CourseClassAsyncWrapper>
            <div className="flex flex-col gap-1 w-full">
                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                    {t('show archived students')}
                </span>
                <Switch
                    size="small"
                    checked={!!filter.include_archived}
                    onChange={(checked) => handleChange('include_archived', checked ? 1 : undefined)}
                    className="w-fit"
                />
            </div>
            <div className="flex flex-col gap-1 w-full">
                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                    {t('status')}
                </span>
                <div className="flex flex-wrap gap-2">
                    <FilterChip active={isAll} onClick={() => handleChange('active', undefined)}>
                        {t('all')}
                    </FilterChip>
                    {[
                        {value: 0, label: 'temporarily inactive'},
                        {value: 1, label: 'active'},
                    ].map((e) => (
                        <FilterChip
                            key={e.value}
                            active={!isAll && activeValue === String(e.value)}
                            onClick={() => handleChange('active', String(e.value))}>
                            {t(e.label)}
                        </FilterChip>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default StudentFilter
